import { ILandingGeometry, IStairCalculationResult, IStairConfig, IStepGeometry } from '@stair-craft/common/util';

import { horizontalDim, verticalDim } from './dim-builders';
import { pushElevationRunLengths, pushElevationTreadMarks } from './elevation-marks';
import { IElevationAcross, IElevationMap, pushElevationRailings, pushElevationSupports } from './elevation-railing';
import {
    EColorRef,
    ETextAlign,
    ETextBaseline,
    IPaneRect,
    IPlanPrimitive,
    IPoint,
    IProjectionOutput,
    IViewFit,
    emptyProjection,
} from './plan-primitive';
import {
    F_400_9,
    F_400_10,
    F_400_11,
    IElevationFrame,
    IMapFn,
    PANE_LABEL_INSET,
    RISER_THICKNESS,
    SIDE_VIEW_REF_HEIGHT_MM,
    STRINGER_THICKNESS,
    elevationFrame,
    fillRect,
    fillStrokeRect,
    level,
    line,
    note,
    strokePoly,
    text,
} from './side-view-projection';
import { ITextMeasurer } from './text-measurer';

interface IBox2 {
    xMin: number;
    xMax: number;
    yMin: number;
    yMax: number;
    kind: 'step' | 'landing' | 'winder';
    isParallel: boolean;
    rotation: number;
}

interface ILuFlightGeom {
    parallelByY: IBox2[];
    lowStep: IBox2;
    highStep: IBox2;
    sign: number;
    xBack: (b: IBox2) => number;
    xFront: (b: IBox2) => number;
    yFloorP: number;
    xStartFlight: number;
    xEndFlight: number;
    dxFlight: number;
    dyFlight: number;
    cosA: number;
    sinA: number;
    /** False for an open stair: the flight shows no riser boards. */
    risers: boolean;
}

interface ILuDimCtx {
    wx: IMapFn;
    wy: IMapFn;
    worldXMin: number;
    worldXMax: number;
    worldXMargin: number;
    floorH: number;
    stackTopY: number;
    splitX: number;
    parallelHighEndIsRight: boolean;
    result: IStairCalculationResult;
    /** Where the angle and Blondel labels start: pinned to the pane top, or above the drawing under a sheet fit. */
    labels: { x: number; angleY: number; blondelX: number; blondelY: number };
}

/** Extracted from `projectSideViewLU` to keep its complexity low: base X extent of a step projected onto the chosen axis. */
function luBaseXBounds(step: IStepGeometry, axis: 'z' | 'x'): { xMin: number; xMax: number } {
    const rot: number = ((step.rotation % 360) + 360) % 360;
    if (axis === 'z') {
        if (rot === 0 || rot === 180) {
            return { xMin: step.position.x, xMax: step.position.x + step.treadWidth };
        }
        const cx: number = step.position.x + step.treadWidth / 2;
        return { xMin: cx - step.treadDepth / 2, xMax: cx + step.treadDepth / 2 };
    }
    if (rot === 0 || rot === 180) {
        return { xMin: step.position.z, xMax: step.position.z + step.treadDepth };
    }
    const cz: number = step.position.z + step.treadDepth / 2;
    return { xMin: cz - step.treadWidth / 2, xMax: cz + step.treadWidth / 2 };
}

/** Extracted from `projectSideViewLU` to keep its complexity low: is this step part of the parallel (sawtooth) flight? */
function luIsParallelStep(s: IStepGeometry, axis: 'z' | 'x'): boolean {
    if (s.isLanding || s.isWinder) {
        return false;
    }
    const rot: number = ((s.rotation % 360) + 360) % 360;
    return axis === 'z' ? rot === 90 || rot === 270 : rot === 0 || rot === 180;
}

/** Extracted from `projectSideViewLU` to keep its complexity low: X extent for a step, snapping winders to the stack span. */
function luXBoundsForStep(step: IStepGeometry, axis: 'z' | 'x', stackXMin: number, stackXMax: number): { xMin: number; xMax: number } {
    if (step.isWinder && isFinite(stackXMin) && isFinite(stackXMax)) {
        return { xMin: stackXMin, xMax: stackXMax };
    }
    return luBaseXBounds(step, axis);
}

/** Extracted from `projectSideViewLU` to keep its complexity low: X span covered by the stacked (perpendicular) flight steps. */
function computeStackXBounds(steps: IStepGeometry[], axis: 'z' | 'x'): { stackXMin: number; stackXMax: number } {
    let stackXMin: number = Infinity;
    let stackXMax: number = -Infinity;
    for (const s of steps) {
        const rot: number = ((s.rotation % 360) + 360) % 360;
        const isStackStep: boolean = axis === 'z' ? rot === 0 || rot === 180 : rot === 90 || rot === 270;
        if (s.isLanding || s.isWinder || !isStackStep) {
            continue;
        }
        const b: { xMin: number; xMax: number } = luBaseXBounds(s, axis);
        stackXMin = Math.min(stackXMin, b.xMin);
        stackXMax = Math.max(stackXMax, b.xMax);
    }
    return { stackXMin, stackXMax };
}

/** Extracted from `projectSideViewLU` to keep its complexity low: highest walking surface of the stacked flight. */
function computeStackTopY(steps: IStepGeometry[], axis: 'z' | 'x'): number {
    const stackSteps: IStepGeometry[] = steps.filter((s: IStepGeometry) => !s.isLanding && !s.isWinder && !luIsParallelStep(s, axis));
    if (stackSteps.length === 0) {
        return 0;
    }
    // `position.y` is the walking surface — the top of the tread, as the 3D model takes it.
    return stackSteps.reduce((acc: number, s: IStepGeometry) => Math.max(acc, s.position.y), 0);
}

/** Extracted from `projectSideViewLU` to keep its complexity low: landing top Y (real landing or inferred from the second flight). */
function computeLULandingTop(result: IStairCalculationResult, parallelStepsList: IStepGeometry[]): number {
    if (result.geometry.landings.length > 0) {
        const ld: ILandingGeometry = result.geometry.landings[0];
        // A landing's `position.y` is its walking surface, like a tread's.
        return Math.max(0, ld.position.y);
    }
    const secondFlightSteps: IStepGeometry[] = parallelStepsList.filter((s: IStepGeometry) => ((s.rotation % 360) + 360) % 360 !== 0);
    if (secondFlightSteps.length > 0) {
        const lowest: IStepGeometry = secondFlightSteps.reduce(
            (acc: IStepGeometry, s: IStepGeometry) => (s.position.y < acc.position.y ? s : acc),
            secondFlightSteps[0]
        );
        return Math.max(0, lowest.position.y - lowest.riserHeight);
    }
    return 0;
}

/** Extracted from `projectSideViewLU` to keep its complexity low: classifies a step box (landing / winder / step). */
function boxKind(step: IStepGeometry): IBox2['kind'] {
    if (step.isLanding) {
        return 'landing';
    }
    if (step.isWinder) {
        return 'winder';
    }
    return 'step';
}

/** Extracted from `projectSideViewLU` to keep its complexity low: fill colour for a step box kind. */
function boxFill(kind: IBox2['kind']): EColorRef {
    if (kind === 'landing') {
        return EColorRef.Landing;
    }
    if (kind === 'winder') {
        return EColorRef.Winder;
    }
    return EColorRef.Step;
}

/** The view's horizontal millimetre of a plan point: X looking along Z, Z looking along X — mirrored as the boxes are. */
function luAcross(axis: 'z' | 'x', mirrored: boolean): IElevationAcross {
    if (axis === 'z') {
        return (point: { x: number; z: number }): number => point.x;
    }
    return mirrored ? (point: { x: number; z: number }): number => -point.z : (point: { x: number; z: number }): number => point.z;
}

/** Extracted from `projectSideViewLU` to keep its complexity low: builds the projected box list for steps + landings. */
function buildLUBoxes(result: IStairCalculationResult, axis: 'z' | 'x', stackXMin: number, stackXMax: number, landingTop: number): IBox2[] {
    const boxes: IBox2[] = [];
    for (const step of result.geometry.steps) {
        const { xMin, xMax } = luXBoundsForStep(step, axis, stackXMin, stackXMax);
        const yTop: number = step.position.y;
        const yBot: number = step.position.y - step.riserHeight;
        const isParallel: boolean = luIsParallelStep(step, axis);
        const rot: number = ((step.rotation % 360) + 360) % 360;
        const parallelFloor: number = rot === 0 ? 0 : landingTop;
        boxes.push({
            xMin,
            xMax,
            isParallel,
            yMin: isParallel ? parallelFloor : yBot,
            yMax: yTop,
            kind: boxKind(step),
            rotation: rot,
        });
    }
    for (const ld of result.geometry.landings) {
        const ldMin: number = axis === 'z' ? ld.position.x : ld.position.z;
        const ldMax: number = axis === 'z' ? ld.position.x + ld.width : ld.position.z + ld.depth;
        boxes.push({
            xMin: ldMin,
            xMax: ldMax,
            yMin: ld.position.y - result.riserHeight,
            yMax: ld.position.y,
            kind: 'landing',
            isParallel: false,
            rotation: 0,
        });
    }
    return boxes;
}

/** Extracted from `projectSideViewLU` to keep its complexity low: mirrors box X extents (left-turn X-axis view) and the stack span. */
function mirrorLUBoxes(boxes: IBox2[], stackXMin: number, stackXMax: number): { stackXMin: number; stackXMax: number } {
    for (const b of boxes) {
        const newMin: number = -b.xMax;
        const newMax: number = -b.xMin;
        b.xMin = newMin;
        b.xMax = newMax;
    }
    if (isFinite(stackXMin) && isFinite(stackXMax)) {
        return { stackXMin: -stackXMax, stackXMax: -stackXMin };
    }
    return { stackXMin, stackXMax };
}

/** Extracted from `projectSideViewLU` to keep its complexity low: world X range incl. margin, or null if there are no finite boxes. */
function computeLUWorldBounds(boxes: IBox2[]): { worldXMin: number; worldXMax: number; worldXMargin: number } | null {
    let worldXMin: number = Infinity;
    let worldXMax: number = -Infinity;
    for (const b of boxes) {
        worldXMin = Math.min(worldXMin, b.xMin);
        worldXMax = Math.max(worldXMax, b.xMax);
    }
    if (!isFinite(worldXMin) || !isFinite(worldXMax)) {
        return null;
    }
    const worldXMargin: number = Math.max(200, (worldXMax - worldXMin) * 0.06);
    return { worldXMin: worldXMin - worldXMargin, worldXMax: worldXMax + worldXMargin, worldXMargin };
}

/** Extracted from `projectSideViewLU` to keep its complexity low: the high end of the parallel flight and its direction. */
function computeParallelHighEnd(
    parallelBoxesAll: IBox2[],
    config: IStairConfig,
    axis: 'z' | 'x',
    worldXMax: number
): { x: number; isRight: boolean } {
    let parallelHighEndX: number = worldXMax;
    let parallelHighEndIsRight: boolean = true;
    if (parallelBoxesAll.length > 0) {
        const isUShapeSide: boolean = config.type === 'u-shape' && axis === 'x';
        const anchorBoxes: IBox2[] = isUShapeSide ? parallelBoxesAll.filter((b: IBox2) => b.rotation !== 0) : parallelBoxesAll;
        const orderSet: IBox2[] = anchorBoxes.length > 0 ? anchorBoxes : parallelBoxesAll;
        const parallelByYOrder: IBox2[] = [...orderSet].sort((a: IBox2, b: IBox2) => a.yMax - b.yMax);
        const lowMidPx: number = (parallelByYOrder[0].xMin + parallelByYOrder[0].xMax) / 2;
        const highBox: IBox2 = parallelByYOrder[parallelByYOrder.length - 1];
        const highMidPx: number = (highBox.xMin + highBox.xMax) / 2;
        parallelHighEndIsRight = highMidPx >= lowMidPx;
        parallelHighEndX = parallelHighEndIsRight ? highBox.xMax : highBox.xMin;
    }
    return { x: parallelHighEndX, isRight: parallelHighEndIsRight };
}

/** Extracted from `projectSideViewLU` to keep its complexity low: X where the stacked flight meets the parallel flight. */
function computeSplitX(
    stackXMin: number,
    stackXMax: number,
    parallelHighEndIsRight: boolean,
    worldXMin: number,
    worldXMargin: number
): number {
    if (!isFinite(stackXMin) || !isFinite(stackXMax)) {
        return worldXMin + worldXMargin;
    }
    return parallelHighEndIsRight ? stackXMax : stackXMin;
}

/** Extracted from `projectSideViewLU` to keep its complexity low: draws the upper-floor slab marker above the parallel flight end. */
function pushLUUpperFloor(
    primitives: IPlanPrimitive[],
    wx: IMapFn,
    wy: IMapFn,
    ws: IMapFn,
    floorH: number,
    slab: number,
    parallelHighEndX: number,
    parallelHighEndIsRight: boolean
): { slabFromX: number; slabToX: number } {
    const slabTop: number = floorH + slab;
    const slabSpan: number = 240;
    const slabFromX: number = parallelHighEndIsRight ? parallelHighEndX : parallelHighEndX - slabSpan;
    const slabToX: number = parallelHighEndIsRight ? parallelHighEndX + slabSpan : parallelHighEndX;
    primitives.push(fillRect(wx(slabFromX), wy(slabTop), ws(slabToX - slabFromX), ws(slab), EColorRef.Ink, 0.08));
    primitives.push(line([wx(slabFromX), wy(floorH)], [wx(slabToX), wy(floorH)], EColorRef.Ink, 1));
    primitives.push(line([wx(slabFromX), wy(slabTop)], [wx(slabToX), wy(slabTop)], EColorRef.Ink, 1));
    const slabEdgeX: number = parallelHighEndIsRight ? slabFromX : slabToX;
    primitives.push(line([wx(slabEdgeX), wy(floorH)], [wx(slabEdgeX), wy(slabTop)], EColorRef.Ink, 1));
    return { slabFromX, slabToX };
}

/** Extracted from `projectSideViewLU` to keep its complexity low: fills the non-parallel step/landing/winder boxes. */
function pushLUStepBoxes(primitives: IPlanPrimitive[], boxes: IBox2[], wx: IMapFn, wy: IMapFn, ws: IMapFn): void {
    const sorted: IBox2[] = [...boxes].sort((a: IBox2, b: IBox2) => a.yMin - b.yMin);
    for (const b of sorted) {
        if (b.isParallel) {
            continue;
        }
        const baseFill: EColorRef = boxFill(b.kind);
        primitives.push(
            fillStrokeRect(wx(b.xMin), wy(b.yMax), ws(b.xMax - b.xMin), ws(b.yMax - b.yMin), baseFill, EColorRef.StepStroke, 1)
        );
    }
}

/** Extracted from `projectSideViewLU` to keep its complexity low: groups parallel boxes into flights (U-shape splits by rotation). */
function computeLUFlightGroups(parallelAll: IBox2[], config: IStairConfig, axis: 'z' | 'x'): IBox2[][] {
    if (config.type === 'u-shape' && axis === 'x') {
        return [parallelAll.filter((b: IBox2) => b.rotation === 0), parallelAll.filter((b: IBox2) => b.rotation !== 0)].filter(
            (g: IBox2[]) => g.length > 0
        );
    }
    if (parallelAll.length > 0) {
        return [parallelAll];
    }
    return [];
}

/** Extracted from `projectSideViewLU` to keep its complexity low: косоур sawtooth stringer polygon for a parallel flight. */
function pushLUKosourStringer(primitives: IPlanPrimitive[], wx: IMapFn, wy: IMapFn, geom: ILuFlightGeom): void {
    const { parallelByY, lowStep, highStep, sign, xBack, xFront, yFloorP, xStartFlight, xEndFlight, dxFlight, cosA, sinA } = geom;
    const yBottomAtFar: number = highStep.yMax - STRINGER_THICKNESS / Math.max(0.001, cosA);
    const xFloorCrossOff: number = sinA > 0.001 ? STRINGER_THICKNESS / sinA : 0;
    const points: IPoint[] = [];
    points.push([wx(xStartFlight), wy(yFloorP)]);
    points.push([wx(xStartFlight), wy(lowStep.yMax)]);
    for (let i: number = 0; i < parallelByY.length; i++) {
        const cur: IBox2 = parallelByY[i];
        points.push([wx(xFront(cur)), wy(cur.yMax)]);
        if (i + 1 < parallelByY.length) {
            const nxt: IBox2 = parallelByY[i + 1];
            points.push([wx(xBack(nxt)), wy(nxt.yMax)]);
        }
    }
    if (yBottomAtFar > yFloorP) {
        points.push([wx(xEndFlight), wy(yBottomAtFar)]);
        if (xFloorCrossOff > 0 && xFloorCrossOff < Math.abs(dxFlight)) {
            points.push([wx(xStartFlight + sign * xFloorCrossOff), wy(yFloorP)]);
        }
    }
    points.push([wx(xStartFlight), wy(yFloorP)]);
    primitives.push(strokePoly(points, EColorRef.Paper2, EColorRef.Ink2, 1.4));
}

/** Extracted from `projectSideViewLU` to keep its complexity low: косоур risers + nosed tread boards for a parallel flight. */
function pushLUKosourSteps(primitives: IPlanPrimitive[], wx: IMapFn, wy: IMapFn, treadT: number, nose: number, geom: ILuFlightGeom): void {
    const { parallelByY, sign, xBack, xFront, yFloorP } = geom;
    // Risers at each step's BACK edge; an open stair has none
    for (let i: number = 0; geom.risers && i < parallelByY.length; i++) {
        const cur: IBox2 = parallelByY[i];
        const prevTop: number = i > 0 ? parallelByY[i - 1].yMax : yFloorP;
        const xBackPos: number = xBack(cur);
        const xR1: number = wx(xBackPos - sign * RISER_THICKNESS);
        const xR2: number = wx(xBackPos);
        const xLo: number = Math.min(xR1, xR2);
        const xHi: number = Math.max(xR1, xR2);
        const yT: number = wy(cur.yMax - treadT);
        const yB: number = wy(prevTop);
        if (yB > yT) {
            primitives.push(fillStrokeRect(xLo, yT, xHi - xLo, yB - yT, EColorRef.Landing, EColorRef.StepStroke, 1));
        }
    }
    // Treads with nose overhang on the BACK side
    for (const cur of parallelByY) {
        const xWithNose: number = xBack(cur) - sign * nose;
        const xF: number = xFront(cur);
        const xLo: number = Math.min(xWithNose, xF);
        const xHi: number = Math.max(xWithNose, xF);
        const yT: number = wy(cur.yMax);
        const yB: number = wy(cur.yMax - treadT);
        primitives.push(fillStrokeRect(wx(xLo), yT, wx(xHi) - wx(xLo), yB - yT, EColorRef.Step, EColorRef.StepStroke, 1));
    }
}

/** Extracted from `projectSideViewLU` to keep its complexity low: тетива side-plank polygon + housed dashed treads for a parallel flight. */
function pushLUTetivaFlight(primitives: IPlanPrimitive[], wx: IMapFn, wy: IMapFn, geom: ILuFlightGeom): void {
    const { parallelByY, lowStep, highStep, sign, xBack, xFront, yFloorP, xStartFlight, xEndFlight, dxFlight, dyFlight, cosA } = geom;
    const slopeAbs: number = dyFlight / Math.max(1, Math.abs(dxFlight));
    const offsetTop: number = lowStep.yMax - yFloorP;
    const planeThickness: number = STRINGER_THICKNESS / Math.max(0.001, cosA);
    const offsetBottom: number = offsetTop - planeThickness;
    const yBottomAtFar: number = highStep.yMax - planeThickness;

    const points: IPoint[] = [];
    points.push([wx(xStartFlight), wy(yFloorP)]);
    points.push([wx(xStartFlight), wy(yFloorP + offsetTop)]);
    points.push([wx(xEndFlight), wy(highStep.yMax)]);
    if (yBottomAtFar > yFloorP) {
        points.push([wx(xEndFlight), wy(yBottomAtFar)]);
        if (offsetBottom < 0) {
            const xBottomFloorCross: number = -offsetBottom / Math.max(0.0001, slopeAbs);
            if (xBottomFloorCross > 0 && xBottomFloorCross < Math.abs(dxFlight)) {
                points.push([wx(xStartFlight + sign * xBottomFloorCross), wy(yFloorP)]);
            }
        }
    } else {
        points.push([wx(xEndFlight), wy(yFloorP)]);
    }
    points.push([wx(xStartFlight), wy(yFloorP)]);
    primitives.push(strokePoly(points, EColorRef.Paper2, EColorRef.Ink2, 1.4));

    // Treads housed as dashed lines
    for (let i: number = 0; i < parallelByY.length; i++) {
        const cur: IBox2 = parallelByY[i];
        primitives.push(line([wx(xBack(cur)), wy(cur.yMax)], [wx(xFront(cur)), wy(cur.yMax)], EColorRef.StepStroke, 1, { dash: [4, 3] }));
        if (i > 0) {
            primitives.push(
                line([wx(xBack(cur)), wy(cur.yMax)], [wx(xBack(cur)), wy(parallelByY[i - 1].yMax)], EColorRef.StepStroke, 1, {
                    dash: [4, 3],
                })
            );
        }
    }
}

/** Extracted from `projectSideViewLU` to keep its complexity low: renders one parallel flight (косоур or тетива). */
function pushLUParallelFlight(primitives: IPlanPrimitive[], parallel: IBox2[], config: IStairConfig, wx: IMapFn, wy: IMapFn): void {
    const isKosour: boolean = config.stringerType === 'kosour';
    const treadT: number = config.treadThickness;
    const nose: number = config.noseLength;

    const parallelByY: IBox2[] = [...parallel].sort((a: IBox2, b: IBox2) => a.yMax - b.yMax);
    const lowStep: IBox2 = parallelByY[0];
    const highStep: IBox2 = parallelByY[parallelByY.length - 1];
    const lowMid: number = (lowStep.xMin + lowStep.xMax) / 2;
    const highMid: number = (highStep.xMin + highStep.xMax) / 2;
    const dirRight: boolean = highMid >= lowMid;
    const sign: number = dirRight ? 1 : -1;
    const xBack: (b: IBox2) => number = (b: IBox2): number => (dirRight ? b.xMin : b.xMax);
    const xFront: (b: IBox2) => number = (b: IBox2): number => (dirRight ? b.xMax : b.xMin);

    const yFloorP: number = lowStep.yMin;
    const xStartFlight: number = xBack(lowStep);
    const xEndFlight: number = xFront(highStep);
    const dxFlight: number = xEndFlight - xStartFlight;
    const dyFlight: number = highStep.yMax - yFloorP;
    const angleRad: number = Math.atan2(dyFlight, Math.max(1, Math.abs(dxFlight)));
    const cosA: number = Math.cos(angleRad);
    const sinA: number = Math.sin(angleRad);

    const geom: ILuFlightGeom = {
        parallelByY,
        lowStep,
        highStep,
        sign,
        xBack,
        xFront,
        yFloorP,
        xStartFlight,
        xEndFlight,
        dxFlight,
        dyFlight,
        cosA,
        sinA,
        risers: config.risersEnabled,
    };

    if (isKosour) {
        pushLUKosourStringer(primitives, wx, wy, geom);
        pushLUKosourSteps(primitives, wx, wy, treadT, nose, geom);
    } else {
        pushLUTetivaFlight(primitives, wx, wy, geom);
    }
}

/** Extracted from `projectSideViewLU` to keep its complexity low: floor labels (1st + 2nd, direction-aware). */
function pushLUFloorLabels(
    primitives: IPlanPrimitive[],
    wx: IMapFn,
    wy: IMapFn,
    worldXMin: number,
    floorH: number,
    slabFromX: number,
    slabToX: number,
    parallelHighEndIsRight: boolean
): void {
    primitives.push(
        level(
            text(
                [wx(worldXMin) + 6, wy(0) - 3],
                $localize`:@@planLevelGround:1-Й ЭТАЖ`,
                ETextAlign.Left,
                ETextBaseline.Bottom,
                EColorRef.Ink3,
                F_400_9
            )
        )
    );
    if (parallelHighEndIsRight) {
        primitives.push(
            level(
                text(
                    [wx(slabFromX) + 4, wy(floorH) - 3],
                    $localize`:@@planLevelUpper:2-Й ЭТАЖ`,
                    ETextAlign.Left,
                    ETextBaseline.Bottom,
                    EColorRef.Ink3,
                    F_400_9
                )
            )
        );
    } else {
        primitives.push(
            level(
                text(
                    [wx(slabToX) - 4, wy(floorH) - 3],
                    $localize`:@@planLevelUpper:2-Й ЭТАЖ`,
                    ETextAlign.Right,
                    ETextBaseline.Bottom,
                    EColorRef.Ink3,
                    F_400_9
                )
            )
        );
    }
}

/**
 * Where the angle and Blondel labels go. In a pane of its own the view pins them under the pane
 * title; under a sheet fit the pane is cut to the drawing, so they stand in two rows above the
 * drawing's top and above the run dimensions.
 */
function luLabelAnchors(
    sheetFit: boolean,
    wx: IMapFn,
    wy: IMapFn,
    worldXMin: number,
    floorH: number,
    worldYMax: number,
    padL: number
): { x: number; angleY: number; blondelX: number; blondelY: number } {
    if (!sheetFit) {
        return { x: padL - 8, angleY: PANE_LABEL_INSET + 14, blondelX: wx(worldXMin) + 6, blondelY: PANE_LABEL_INSET + 30 };
    }
    const top: number = Math.min(wy(worldYMax), wy(floorH) - 44) - 30;
    return { x: wx(worldXMin) + 6, angleY: top, blondelX: wx(worldXMin) + 6, blondelY: top + 15 };
}

/** Extracted from `projectSideViewLU` to keep its complexity low: Blondel label, run/rise dims + angle label. */
function pushLUDimsAndLabels(primitives: IPlanPrimitive[], measurer: ITextMeasurer, ctx: ILuDimCtx): void {
    const { wx, wy, worldXMin, worldXMax, worldXMargin, floorH, stackTopY, splitX, parallelHighEndIsRight, labels, result } = ctx;

    // ── Blondel formula label (top-left, below pane title) ──
    primitives.push(
        note(
            text(
                [labels.blondelX, labels.blondelY],
                `h ${String(Math.round(result.riserHeight))} · b ${String(Math.round(result.treadDepth))} · 2h+b ${String(Math.round(result.blondelValue))}`,
                ETextAlign.Left,
                ETextBaseline.Top,
                EColorRef.Ink2,
                F_400_10
            )
        )
    );

    // ── Dimensions ──
    const innerXMin: number = worldXMin + worldXMargin;
    const innerXMax: number = worldXMax - worldXMargin;
    primitives.push(horizontalDim(wx(innerXMin), wy(floorH) - 28, wx(innerXMax), String(Math.round(innerXMax - innerXMin)), measurer));
    if (splitX > innerXMin && splitX < innerXMax) {
        const sawtoothFarX: number = parallelHighEndIsRight ? innerXMax : innerXMin;
        const left: number = Math.min(splitX, sawtoothFarX);
        const right: number = Math.max(splitX, sawtoothFarX);
        primitives.push(horizontalDim(wx(left), wy(floorH) - 14, wx(right), String(Math.round(right - left)), measurer));
    }
    primitives.push(verticalDim(wx(innerXMin) - 22, wy(0), wy(floorH), String(Math.round(floorH)), measurer));
    if (stackTopY > 0 && stackTopY < floorH) {
        primitives.push(verticalDim(wx(innerXMin) - 8, wy(0), wy(stackTopY), String(Math.round(stackTopY)), measurer));
        primitives.push(verticalDim(wx(innerXMin) - 8, wy(stackTopY), wy(floorH), String(Math.round(floorH - stackTopY)), measurer));
    }

    // Angle label (top-left corner)
    primitives.push(
        note(
            text(
                [labels.x, labels.angleY],
                `∠ ${result.stairAngle.toFixed(1)}°`,
                ETextAlign.Left,
                ETextBaseline.Top,
                EColorRef.Ink2,
                F_400_11
            )
        )
    );
}

/**
 * Side view for L/U — TRUE projection: the perpendicular flight stacks as bars,
 * the parallel flight as a sawtooth stringer, the landing between them.
 *   axis='z' → project along Z: flight 1 stacks, flight 2 is sawtooth.
 *   axis='x' → project along X: flight 1 is sawtooth, flight 2 stacks.
 * Pane-local pixels.
 */
export function projectSideViewLU(
    result: IStairCalculationResult,
    config: IStairConfig,
    pane: IPaneRect,
    measurer: ITextMeasurer,
    axis: 'z' | 'x',
    fit?: IViewFit
): IProjectionOutput {
    const slab: number = result.room.floorThickness;
    const floorH: number = result.room.floorHeight;
    const N: number = result.stepCount;
    if (N <= 0 || floorH <= 0) {
        return emptyProjection();
    }

    let { stackXMin, stackXMax } = computeStackXBounds(result.geometry.steps, axis);

    const parallelStepsList: IStepGeometry[] = result.geometry.steps.filter((s: IStepGeometry) => luIsParallelStep(s, axis));
    const landingTop: number = computeLULandingTop(result, parallelStepsList);

    const boxes: IBox2[] = buildLUBoxes(result, axis, stackXMin, stackXMax, landingTop);

    const isMirroredView: boolean = axis === 'x' && config.turnDirection === 'left';
    if (isMirroredView) {
        const mirrored: { stackXMin: number; stackXMax: number } = mirrorLUBoxes(boxes, stackXMin, stackXMax);
        stackXMin = mirrored.stackXMin;
        stackXMax = mirrored.stackXMax;
    }

    const bounds: { worldXMin: number; worldXMax: number; worldXMargin: number } | null = computeLUWorldBounds(boxes);
    if (!bounds) {
        return emptyProjection();
    }
    const { worldXMin, worldXMax, worldXMargin } = bounds;
    const worldYMin: number = -50;
    const worldYMax: number = floorH + slab + 80;

    const worldW: number = worldXMax - worldXMin;
    const worldH: number = worldYMax - worldYMin;

    const padL: number = 56;
    const padR: number = 40;
    const padT: number = 38;
    const padB: number = 50;
    const innerW: number = pane.w - padL - padR;
    const innerH: number = pane.h - padT - padB;
    const frame: IElevationFrame | null = elevationFrame(fit, (): IElevationFrame | null => {
        if (innerW <= 0 || innerH <= 0) {
            return null;
        }
        const own: number = Math.min(innerW / worldW, innerH / Math.max(worldH, SIDE_VIEW_REF_HEIGHT_MM));
        return { scale: own, ox: padL + Math.max(0, (innerW - worldW * own) / 2) - worldXMin * own, oy: pane.h - padB };
    });
    if (!frame) {
        return emptyProjection();
    }
    const { scale, ox, oy }: IElevationFrame = frame;

    const wx: IMapFn = (x: number): number => ox + x * scale;
    const wy: IMapFn = (y: number): number => oy - y * scale;
    const ws: IMapFn = (s: number): number => s * scale;

    const stackTopY: number = computeStackTopY(result.geometry.steps, axis);

    const parallelBoxesAll: IBox2[] = boxes.filter((b: IBox2) => b.isParallel);
    const highEnd: { x: number; isRight: boolean } = computeParallelHighEnd(parallelBoxesAll, config, axis, worldXMax);
    const parallelHighEndX: number = highEnd.x;
    const parallelHighEndIsRight: boolean = highEnd.isRight;
    const splitX: number = computeSplitX(stackXMin, stackXMax, parallelHighEndIsRight, worldXMin, worldXMargin);

    const primitives: IPlanPrimitive[] = [];

    // ── Floor 1 (bottom) ──
    primitives.push(line([wx(worldXMin), wy(0)], [wx(worldXMax), wy(0)], EColorRef.Ink, 1));

    // ── Upper floor — slab edge marker above the END of the parallel flight ──
    const { slabFromX, slabToX } = pushLUUpperFloor(primitives, wx, wy, ws, floorH, slab, parallelHighEndX, parallelHighEndIsRight);

    // ── Step boxes — FILL non-parallel boxes (stack/landing/winder) ──
    pushLUStepBoxes(primitives, boxes, wx, wy, ws);

    // Parallel flight (sawtooth) — direction-aware rendering.
    const parallelAll: IBox2[] = boxes.filter((b: IBox2) => b.isParallel);
    const flightGroups: IBox2[][] = computeLUFlightGroups(parallelAll, config, axis);
    for (const parallel of flightGroups) {
        pushLUParallelFlight(primitives, parallel, { ...config, risersEnabled: result.geometry.risers }, wx, wy);
    }

    // ── Floor labels ──
    pushLUFloorLabels(primitives, wx, wy, worldXMin, floorH, slabFromX, slabToX, parallelHighEndIsRight);

    // ── Railing — from the calculation's railing geometry, as on every elevation ──
    const elevation: IElevationMap = { across: luAcross(axis, isMirroredView), wx, wy };
    pushElevationSupports(primitives, result, elevation);
    pushElevationRailings(primitives, result, config, elevation);
    pushElevationTreadMarks(primitives, result, elevation, measurer);
    pushElevationRunLengths(primitives, result, config, elevation);

    // ── Blondel formula label, run/rise dimensions + angle label ──
    pushLUDimsAndLabels(primitives, measurer, {
        wx,
        wy,
        worldXMin,
        worldXMax,
        worldXMargin,
        floorH,
        stackTopY,
        splitX,
        parallelHighEndIsRight,
        result,
        labels: luLabelAnchors(fit !== undefined, wx, wy, worldXMin, floorH, worldYMax, padL),
    });

    // Millimetre origin: the first floor at world x = 0.
    return { primitives, pxPerMm: scale, origin: { x: ox, y: oy } };
}
